import { useNotification } from 'admin';
import {
  getApiSamplesV1SamplesListRetrieveQueryKey,
  useApiSamplesV1SamplesDestroy,
  useApiSamplesV1SamplesListRetrieveSuspense,
  useApiSamplesV1SamplesUpdate,
  useQueryClient,
} from 'api';
import { useMemo, useState } from 'react';

export const useManageSamples = ({ siteMaterialId, onClose }: { siteMaterialId: string; onClose?: () => void }) => {
  const queryClient = useQueryClient();
  const { open } = useNotification();
  const [selected, setSelected] = useState<number[]>([]);
  const {
    data: { results: samples },
  } = useApiSamplesV1SamplesListRetrieveSuspense(siteMaterialId);
  const { mutate: updateSample } = useApiSamplesV1SamplesUpdate({
    mutation: {
      onError: (error) => {
        open?.({
          message: `Error moving sample: ${error}`,
          type: 'error',
        });
      },
      onSuccess: () => {
        open?.({
          message: 'Samples moved',
          type: 'success',
        });
        queryClient.invalidateQueries({ queryKey: getApiSamplesV1SamplesListRetrieveQueryKey(siteMaterialId) });
      },
    },
  });
  const { mutate: deleteSample } = useApiSamplesV1SamplesDestroy({
    mutation: {
      onError: (error) => {
        open?.({
          message: `Error moving sample: ${error}`,
          type: 'error',
        });
      },
      onSuccess: () => {
        open?.({
          message: 'Samples deleted',
          type: 'success',
        });
        queryClient.invalidateQueries({ queryKey: getApiSamplesV1SamplesListRetrieveQueryKey(siteMaterialId) });
      },
    },
  });

  const isSelected = (sampleId: number) => selected.includes(sampleId);

  const selectedSamples = useMemo(() => {
    const selectedSet = new Set(selected);
    return samples.filter((sample) => selectedSet.has(sample.id));
  }, [samples, selected]);

  const updateSelected = ({ trade_company_site_material }: { trade_company_site_material?: string }) => {
    for (const sample of selectedSamples) {
      updateSample({
        data: {
          ...sample,
          site_material_id: Number(trade_company_site_material),
        },
        sampleId: sample.id,
      });
    }
    onClose?.();
  };

  const deleteSelected = () => {
    for (const sample of selectedSamples) {
      deleteSample({ sampleId: sample.id });
    }
    onClose?.();
  };

  const handleSelect = (sampleId: number) => {
    if (selected.includes(sampleId)) {
      setSelected(selected.filter((s) => s !== sampleId));
    } else {
      setSelected([...selected, sampleId]);
    }
  };

  return {
    deleteSelected,
    isSelected,
    samples,
    select: handleSelect,
    selected: selectedSamples,
    setSelected,
    updateSelected,
  };
};
